import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { Contact, ContactsSelect, Modal, ModalActions, Select, SelectProps } from '@pletly/ui';
import { getName, findFirstTwoUsersInIds } from '@pletly/shared';
import { useAuthHeaders } from '@pletly/feature-auth';

import { OrganisationMemberDataType } from '../../../../../view/components/OrganisationMembersTable';

type CareRecipientMember = OrganisationMemberDataType;

export interface CareRecipientsSelectProps extends Omit<
	SelectProps,
	'onSelect' | 'onPressIn' | 'placeholder' | 'value'
> {
	careRecipientsToSelect: CareRecipientMember[];
	value: string[];
	onSelect: (selectedUuids: string[]) => void;
	placeholder: string;
}

export const CareRecipientsSelect = ({
	careRecipientsToSelect,
	value,
	onSelect,
	placeholder,
	...selectProps
}: CareRecipientsSelectProps) => {
	const { t } = useTranslation();
	const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean();
	const [selectedMemberIdsForModal, setSelectedMemberIdsForModal] = useState<string[]>(value);
	const { getHeaders } = useAuthHeaders();

	if (!isModalOpen && value.length === 0 && selectedMemberIdsForModal.length !== 0) {
		setSelectedMemberIdsForModal(value);
	}

	const contacts: Contact[] = useMemo(
		() =>
			careRecipientsToSelect.map(({ memberId, photoUrl, ...careRecipient }) => ({
				id: memberId,
				name: getName(careRecipient),
				photoSource: photoUrl ? { uri: photoUrl, headers: getHeaders(photoUrl) } : undefined,
			})),
		[careRecipientsToSelect, getHeaders],
	);

	const handleSelect = useCallback(() => {
		onSelect(selectedMemberIdsForModal);
		closeModal();
	}, [onSelect, selectedMemberIdsForModal, closeModal]);

	const handleCancel = useCallback(() => {
		closeModal();
		setSelectedMemberIdsForModal(value);
	}, [value, closeModal]);

	const getSelectedUsersText = () => {
		const { firstSelectedUser, secondSelectedUser } = findFirstTwoUsersInIds(contacts, value);
		if (!firstSelectedUser) {
			return placeholder;
		}
		const othersLength = value.length < 2 ? 0 : value.length - 2;

		return firstSelectedUser.name
			.concat(secondSelectedUser ? `, ${secondSelectedUser.name}` : '')
			.concat(othersLength ? ` ${t('common.and-others', { count: othersLength })}` : '');
	};

	return (
		<>
			<Select onPressIn={openModal} placeholder={getSelectedUsersText()} {...selectProps} />
			<Modal
				visible={isModalOpen}
				onClose={handleCancel}
				title={placeholder}
				actions={
					<ModalActions
						primaryAction={{
							label: t('common.save'),
							onPress: handleSelect,
						}}
						secondaryAction={{
							label: t('common.cancel'),
							onPress: handleCancel,
						}}
					/>
				}
			>
				<ContactsSelect
					selectedIds={selectedMemberIdsForModal}
					setSelectedIds={setSelectedMemberIdsForModal}
					mode="multi"
					contacts={contacts}
				/>
			</Modal>
		</>
	);
};
